<template>
  <div class="dashboard-container">

    <!-- Business Information Form -->
    <div class="form-container">
      <h3>🚀 Business Directory AI Dashboard</h3>
      <v-text-field dark v-model="business.name" label="Business Name" required :class="{'missing': !business.name}"></v-text-field>
      <v-text-field dark v-model="business.website" label="Website URL" required :class="{'missing': !business.website}"></v-text-field>
      <v-text-field dark v-model="business.address" label="Address" required></v-text-field>
      <v-text-field dark v-model="business.phone" label="Phone Number"></v-text-field>
      <v-textarea dark v-model="business.description" label="Business Description"></v-textarea>
      <v-text-field dark v-model="business.keywords" label="Keywords (comma separated)"></v-text-field>
      <v-btn @click="analyzeWebsite" color="primary">Analyze Website for Keywords</v-btn>

      <!-- Suggested Keywords -->
      <div v-if="suggestedKeywords.length">
        <h4>Suggested Keywords</h4>
        <span v-for="(keyword, index) in suggestedKeywords" :key="index" class="keyword-chip" @click="addKeyword(keyword)">
          {{ keyword }}
        </span>
      </div>

      <!-- Dynamic Button: Save or Update -->
      <v-btn v-if="currentEditingIndex === null" @click="submitBusiness" color="success" :disabled="loading">➕ Add Business</v-btn>
      <v-btn v-else @click="updateBusiness(currentEditingIndex)" color="warning">🔄 Update Business</v-btn>
    </div>

    <!-- AI Marketing Strategy -->
    <div v-if="marketingStrategy" class="ai-container">
      <h3>📊 AI-Generated Marketing Strategy</h3>
      <p>{{ marketingStrategy }}</p>
    </div>

    <!-- 🔥 Directory Listings Chart -->
    <div class="chart-container">
      <h3>Listings Submission Progress</h3>
      <canvas ref="submissionChart"></canvas>
    </div>

    <!-- 📊 Directory Listings Status -->
    <div class="directory-container">
      <h3>Directory Listings Status</h3>
      <!-- <div class="directory-actions">
        <v-btn @click="submitToYext">Submit to Yext</v-btn>
        <v-btn @click="submitFirst(5)">Submit First 5</v-btn>
        <v-btn @click="submitFirst(10)">Submit First 10</v-btn>
        <v-btn @click="submitPending">Submit Pending</v-btn>
        <v-btn @click="submitAll">Submit All</v-btn>
      </div> -->
      <div class="directory-grid">
        <div v-for="(listing, index) in directoryListings" :key="index" class="directory-card">
          <span :class="{'submitted': listing.status === 'Submitted'}">
            {{ listing.platform }} - {{ listing.status }} 
            <span v-if="listing.submittedOn">on {{ listing.submittedOn }}</span>
          </span>
          <v-btn @click="submitIndividual(index)" class="submit-btn">Submit</v-btn>
        </div>
      </div>
    </div>

    <!-- ChatGPT SEO Assistant -->
    <div class="chat-container">
      <h3>🤖 AI Marketing Assistant</h3>
      <textarea v-model="question" placeholder="Ask about your saved businesses..." class="question-box"></textarea>
      <v-btn @click="askGPTAboutData" color="secondary">{{ loading ? "Loading..." : "Ask AI" }}</v-btn>
      <div v-if="response" class="response-box">
        <p>{{ response }}</p>
      </div>
    </div>

    <!-- AI Suggested Next Steps -->
    <div v-if="nextSteps.length" class="ai-container">
      <h3>📌 Next Steps</h3>
      <ul>
        <li v-for="(step, index) in nextSteps" :key="index">
          {{ step }} <v-btn color="green" @click="executeStep(step)">Execute</v-btn>
        </li>
      </ul>
    </div>

    <!-- 🏆 Saved Business Listings (Gamified) -->
    <div class="saved-container">
      <h3>🏆 Saved Business Listings</h3>
      <div class="table-wrapper">
        <table class="animated-table">
          <thead>
            <tr>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            <transition-group name="fade" tag="tbody">
              <tr class="dashboard-container" v-for="(biz, index) in savedBusinesses" :key="index">
                <td>{{ biz.name }}</td>
                <td><a :href="biz.website" target="_blank">{{ biz.website }}</a></td>
                <td :class="biz.status === 'Submitted' ? 'submitted' : 'pending'">
                  {{ biz.status }} 
                  <span v-if="biz.status === 'Submitted'">🏆</span>
                </td>
                <td>{{ biz.submittedOn || 'Not submitted' }}</td>
                <td class="actions">
                  <v-btn @click="editBusiness(index)" class="edit-btn">✏️ Edit</v-btn>
                  <v-btn @click="deleteBusiness(index)" class="delete-btn">🗑️ Delete</v-btn>
                </td>
              </tr>
            </transition-group>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>


<script>
import axios from "axios";
import Chart from "chart.js";

export default {
  data() {
    return {
      business: {
        name: "",
        website: "",
        address: "",
        phone: "",
        description: "",
        keywords: "",
      },
      suggestedKeywords: [],
      marketingStrategy: "",
      nextSteps: [],
      directoryListings: [
        { platform: "Yelp", status: "Pending" },
        { platform: "Google My Business", status: "Pending" },
        { platform: "Bing Places", status: "Pending" },
        { platform: "Apple Maps", status: "Pending" },
        { platform: "Foursquare", status: "Pending" },
        { platform: "Angie's List", status: "Pending" },
        { platform: "Superpages", status: "Pending" },
        { platform: "Hotfrog", status: "Pending" },
        { platform: "Manta", status: "Pending" },
        { platform: "YellowPages", status: "Pending" },
        { platform: "Local.com", status: "Pending" },
        { platform: "Citysearch", status: "Pending" },
        { platform: "MerchantCircle", status: "Pending" },
        { platform: "EZlocal", status: "Pending" },
        { platform: "ShowMeLocal", status: "Pending" },
        { platform: "Brownbook", status: "Pending" },
        { platform: "Cylex", status: "Pending" },
        { platform: "CitySquares", status: "Pending" },
        { platform: "Tupalo", status: "Pending" },
        { platform: "GetFave", status: "Pending" },
      ],
      loading: false,
      savedBusinesses: JSON.parse(localStorage.getItem("savedBusinesses")) || [],
      gptApiKey: "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",
      googleBusinessApiKey: "AIzaSyBboVjZMYbpCulM_NhAuW5I_e6ZwD5f8qA",
      submissionChart: null,
    };
  },
  methods: {
    async analyzeWebsite() {
      this.suggestedKeywords = await this.askGPT(
        `Analyze the website ${this.business.website} and suggest keywords for SEO optimization.`
      ).then((res) => res.split(", "));
    },
    addKeyword(keyword) {
      if (!this.business.keywords.includes(keyword)) {
        this.business.keywords += (this.business.keywords ? ", " : "") + keyword;
      }
    },

    // editBusiness(index) {
    //   this.business = { ...this.savedBusinesses[index] };
    //   this.savedBusinesses.splice(index, 1);
    //   localStorage.setItem("savedBusinesses", JSON.stringify(this.savedBusinesses));
    // },

    deleteBusiness(index) {
      this.savedBusinesses.splice(index, 1);
      localStorage.setItem("savedBusinesses", JSON.stringify(this.savedBusinesses));
    },
    
    updateBusiness(index) {
  if (!this.business.name || !this.business.website) {
    alert("Business Name and Website are required.");
    return;
  }
  this.savedBusinesses[index] = { ...this.business };
  localStorage.setItem("savedBusinesses", JSON.stringify(this.savedBusinesses));
  this.resetForm();
},
editBusiness(index) {
  this.business = { ...this.savedBusinesses[index] };
  this.currentEditingIndex = index;
},

resetForm() {
  this.business = {
    name: "",
    website: "",
    address: "",
    phone: "",
    description: "",
    keywords: "",
  };
  this.currentEditingIndex = null;
},
    async askGPT(question) {
      try {
        const response = await axios.post(
          "https://api.openai.com/v1/chat/completions",
          {
            model: "gpt-4o",
            messages: [{ role: "user", content: question }],
            max_tokens: 3000,
            temperature: 0.5,
          },
          {
            headers: { Authorization: `Bearer ${this.gptApiKey}` },
          }
        );
        return response.data.choices[0].message.content.trim();
      } catch (error) {
        console.error("AI error:", error);
        return "Could not generate response.";
      }
    },

    async submitBusiness() {
  this.loading = true;

  if (!this.business.name || !this.business.website) {
    alert("Business Name and Website are required.");
    this.loading = false;
    return;
  }

  let businessCopy = {
    ...this.business,
    status: "Submitted",
    submittedOn: new Date().toLocaleDateString(),
  };

  if (this.currentEditingIndex !== null) {
    this.savedBusinesses[this.currentEditingIndex] = businessCopy;
  } else {
    this.savedBusinesses.push(businessCopy);
  }

  localStorage.setItem("savedBusinesses", JSON.stringify(this.savedBusinesses));

  // ✅ Fix: Ensure function is async to use await properly
  this.marketingStrategy = await this.askGPT(
    `Generate a marketing strategy for '${this.business.name}'`
  );

  this.nextSteps = await this.askGPT(
    `Suggest next steps for marketing '${this.business.name}'`
  ).then((res) => res.split("\n"));

  this.resetForm();
  this.loading = false;
},



    async submitIndividual(index) {
      this.directoryListings[index].status = "Submitted";
      this.directoryListings[index].submittedOn = new Date().toLocaleDateString();
      this.updateChart();
    },
    updateSubmissionStatus(platform) {
      let index = this.directoryListings.findIndex((listing) => listing.platform === platform);
      if (index !== -1) {
        this.directoryListings[index].status = "Submitted";
        this.directoryListings[index].submittedOn = new Date().toLocaleDateString();
        this.updateChart();
      }
    },
    async askGPTAboutData() {
  if (!this.question) return;
  
  this.loading = true;
  this.response = await this.askGPT(`Based on the saved business listings: ${JSON.stringify(this.savedBusinesses)}, answer the following: ${this.question}`);
  this.loading = false;
},
    updateChart() {
      if (this.$refs.submissionChart) {
        if (this.submissionChart) {
          this.submissionChart.destroy();
        }
        let submitted = this.directoryListings.filter((l) => l.status === "Submitted").length;
        let pending = this.directoryListings.length - submitted;

        this.submissionChart = new Chart(this.$refs.submissionChart, {
          type: "doughnut",
          data: {
            labels: ["Submitted", "Pending"],
            datasets: [
              {
                data: [submitted, pending],
                backgroundColor: ["#4CAF50", "#FF9800"],
              },
            ],
          },
        });
      }
    },
  },
  mounted() {
    this.updateChart();
  },
};
</script>



<style>
/* 🌌 Futuristic AI Dashboard Theme */
.dashboard-container {
  width: 100%;
  margin: auto;
  background: linear-gradient(135deg, rgba(10, 10, 30, 0.95), rgba(20, 20, 60, 0.95));
  padding: 40px;
  color: #fff;
}

/* 🚀 Titles */
h3 {
  text-align: center;
  font-size: 1.8em;
  font-weight: bold;
  text-transform: uppercase;
  color: #0ff;
  text-shadow: 0px 0px 8px rgba(0, 255, 255, 0.8);
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;

}
/* 🚀 Futuristic AI Dashboard Theme */
.dashboard-container {
  width: 100%;
  margin: auto;
  background: linear-gradient(135deg, rgba(10, 10, 30, 0.95), rgba(20, 20, 60, 0.95));
  padding: 40px;
  color: #fff;
}
.chat-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
}

.question-box {
  width: 100%;
  height: 100px;
  margin: 10px 0;
  padding: 10px;
  font-size: 16px;
}
/* Highlight missing required fields */
.missing {
  border: 2px solid red !important;
  animation: pulse 1s infinite alternate;
}

/* Trophy Animation */
.submitted {
  color: #4CAF50;
  font-weight: bold;
  text-shadow: 0px 0px 8px rgba(76, 255, 0, 0.8);
}

.submitted span {
  animation: pop 0.5s ease-in-out;
}

@keyframes pop {
  0% { transform: scale(0.8); }
  100% { transform: scale(1.2); }
}

.pending {
  color: #FFA500;
}

/* Glowing AI Buttons */
.v-btn {
  transition: all 0.3s ease-in-out;
}

.v-btn:hover {
  box-shadow: 0px 0px 10px rgba(0, 255, 255, 0.8);
}

/* Fade Animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.chart-container {
  margin-top: 20px;
  padding: 100px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  max-width: 100%;
  box-shadow: inset 0px 0px 8px rgba(0, 255, 255, 0.3);
}

.submission-chart {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.ai-container h3,
.chart-container h3,
.directory-container h3,
.saved-container h3 {
  font-size: 1.5em;
  margin-bottom: 10px;
}
/* 📄 Form Containers */
.form-container,
.saved-container,
.chat-container,
.ai-container {
  background: rgba(0, 0, 0, 0.5);
  padding: 40px;
  margin-bottom: 20px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  box-shadow: inset 0px 0px 8px rgba(0, 255, 255, 0.3);
}

/* 🛸 Input Fields */
.v-text-field input,
.v-textarea textarea {
  background: rgba(0, 0, 0, 0.3);
  padding: 30px 30px 30px 30px;
  margin: 40px 40px 40px 40px;
  color: #0ff !important;
  font-size: 20px;
  border-radius: 5px;
  border: 1px solid rgba(0, 255, 255, 0.5);
}

.v-text-field input::placeholder,
.v-textarea textarea::placeholder {
  color: rgba(0, 255, 255, 0.5);
}

.animated-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  overflow: hidden;
}

.animated-table th,
.animated-table td {
  padding: 14px;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.animated-table th {
  background: linear-gradient(45deg, #0ff, #00f);
  color: white;
  text-transform: uppercase;
}

.animated-table tr {
  transition: all 0.3s ease-in-out;
}

.animated-table tr:hover {
  background: rgba(0, 255, 255, 0.1);
}

/* ✅ Submission Status */
.submitted {
  color: #4CAF50;
  font-weight: bold;
  text-shadow: 0px 0px 5px rgba(76, 255, 0, 0.8);
}

/* 🎯 Directory Grid */
.directory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  padding: 10px;
}

/* 📌 Directory Cards */
.directory-card {
  background: rgba(0, 0, 0, 0.3);
  padding: 15px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  box-shadow: 0px 0px 8px rgba(0, 255, 255, 0.3);
  text-align: center;
  transition: all 0.3s ease-in-out;
}

.directory-card:hover {
  background: rgba(0, 255, 255, 0.1);
  transform: scale(1.05);
}

/* 🎮 Submit Button for Directories */
.submit-btn {
  margin-top: 8px;
  background: linear-gradient(45deg, #0ff, #00f);
  transition: all 0.3s ease-in-out;
}

.submit-btn:hover {
  background: linear-gradient(45deg, #00f, #0ff);
  box-shadow: 0px 0px 10px rgba(0, 255, 255, 0.8);
}

/* ✨ Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

</style>
